import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavBar from '../dashboard/components/TopNavBar';
import Icon from '../../components/AppIcon';
import { hospitalSettingsAPI } from '../../api';
import RoleGuard from '../../components/guards/RoleGuard';
import { useAuth } from '../../state/auth/useAuth';
import NavigationPreventer from '../../components/NavigationPreventer';
import Logo from '../../components/Logo';

const initialBasicInfo = {
  name: '',
  hospitalCode: '',
  registrationNumber: '',
  type: 'Private',
  registrationType: 'Private Limited',
  address: {
    street: '',
    area: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'India'
  },
  contact: {
    phone: '',
    mobile: '',
    email: '',
    website: '',
    emergencyNumber: ''
  },
  taxDetails: {
    gstNumber: '',
    panNumber: '',
    tanNumber: '',
    cinNumber: ''
  }
};

const initialInvoiceConfig = {
  invoicePrefix: 'INV',
  invoiceFooter: '',
  paymentTerms: 'Payment due within 30 days',
  defaultTaxRate: 18,
  bankDetails: {
    bankName: '',
    accountName: '',
    accountNumber: '',
    ifscCode: '',
    branchName: ''
  }
};

export default function HospitalSettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [navTab, setNavTab] = useState('settings');
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [settingsId, setSettingsId] = useState(null);
  const [basicInfo, setBasicInfo] = useState(initialBasicInfo);
  const [invoiceConfig, setInvoiceConfig] = useState(initialInvoiceConfig);
  const [logoPreview, setLogoPreview] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleNavTabChange = (tabId) => {
    setNavTab(tabId);
    switch(tabId) {
      case 'dashboard':
        navigate('/dashboard');
        break;
      case 'master-data':
        navigate('/admin/dashboard');
        break;
      case 'patients':
        navigate('/users');
        break;
      case 'inventory':
        navigate('/inventory');
        break;
      case 'reports':
        navigate('/accounting');
        break;
      case 'finance':
        navigate('/finance');
        break;
      case 'settings':
        break;
      case 'profile':
        console.log('Profile navigation - route not yet configured');
        break;
      default:
        break;
    }
  };

  const tabs = useMemo(() => ([
    { id: 'basic', label: 'Basic Info', icon: 'Building2' },
    { id: 'logo', label: 'Logo', icon: 'Image' },
    { id: 'invoice', label: 'Invoice Config', icon: 'FileText' }
  ]), []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await hospitalSettingsAPI.getHospitalSettings();
        const data = res?.data;
        // Some backends may wrap in { data: { settings } } or return array; handle defensively
        const settings = Array.isArray(data) ? (data[0] || null) : (data?.settings || data || null);
        if (settings) {
          setSettingsId(settings.id || settings._id || settings.uuid || null);
          setBasicInfo({
            ...initialBasicInfo,
            name: settings.name || '',
            hospitalCode: settings.hospitalCode || '',
            registrationNumber: settings.registrationNumber || '',
            type: settings.type || 'Private',
            registrationType: settings.registrationType || 'Private Limited',
            address: { ...initialBasicInfo.address, ...(settings.address || {}) },
            contact: { ...initialBasicInfo.contact, ...(settings.contact || {}) },
            taxDetails: { ...initialBasicInfo.taxDetails, ...(settings.taxDetails || {}) }
          });
          const inv = settings.invoiceConfig || {};
          setInvoiceConfig({
            ...initialInvoiceConfig,
            ...inv
          });
          if (settings.logo?.url) {
            setLogoPreview(settings.logo.url);
          }
        }
      } catch (e) {
        // If 404 or not found, keep empty state for initial setup
        setError(e.message || 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setBasic = (path, value) => {
    setBasicInfo(prev => {
      const next = { ...prev };
      const segments = path.split('.');
      if (segments.length === 2) {
        const [root, key] = segments;
        next[root] = { ...prev[root], [key]: value };
      } else {
        next[path] = value;
      }
      return next;
    });
  };

  const buildBasicPayload = () => ({
    name: basicInfo.name?.trim(),
    hospitalCode: basicInfo.hospitalCode?.trim(),
    registrationNumber: basicInfo.registrationNumber?.trim(),
    type: basicInfo.type,
    registrationType: basicInfo.registrationType,
    address: { ...basicInfo.address },
    contact: { ...basicInfo.contact },
    taxDetails: { ...basicInfo.taxDetails }
  });

  const handleSaveBasic = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const payload = buildBasicPayload();
      await hospitalSettingsAPI.updateBasicInfo(payload);
      setSuccess('Basic info updated');
    } catch (e) {
      setError(e.message);
    } finally { setSaving(false); }
  };

  const handleSaveInvoice = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await hospitalSettingsAPI.updateInvoiceConfig({
        invoicePrefix: invoiceConfig.invoicePrefix,
        invoiceFooter: invoiceConfig.invoiceFooter,
        paymentTerms: invoiceConfig.paymentTerms,
        defaultTaxRate: invoiceConfig.defaultTaxRate,
        bankDetails: { ...invoiceConfig.bankDetails }
      });
      setSuccess('Invoice configuration updated');
    } catch (e) {
      setError(e.message);
    } finally { setSaving(false); }
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, GIF, WebP)');
      return;
    }
    
    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File size must be less than 5MB');
      return;
    }
    
    setSelectedFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
    setError('');
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleUploadLogo = async () => {
    if (!selectedFile) {
      setError('Please select an image file');
      return;
    }
    setSaving(true); setError(''); setSuccess('');
    try {
      const res = await hospitalSettingsAPI.updateHospitalLogo(selectedFile);
      const newUrl = res?.data?.logo?.url || res?.logo?.url || '';
      if (newUrl) {
        setLogoPreview(newUrl);
        setSelectedFile(null);
      }
      setSuccess('Logo uploaded successfully');
    } catch (e) {
      setError(e.message);
    } finally { setSaving(false); }
  };

  const handleInitialCreate = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const payload = {
        ...buildBasicPayload(),
        invoiceConfig: { ...invoiceConfig }
      };
      const res = await hospitalSettingsAPI.createHospitalSettings(payload);
      const created = res?.data;
      setSettingsId(created?.id || created?._id || null);
      setSuccess('Hospital settings created');
    } catch (e) {
      setError(e.message);
    } finally { setSaving(false); }
  };

  const INP = 'w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-semibold text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] hover:border-[#2D869C] transition-all bg-white';
  const LBL = 'block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide';
  const SEC = 'text-md font-semibold text-gray-600 uppercase tracking-wide mb-3 mt-1';

  const renderBasicForm = () => (
    <div className="space-y-6" style={{ fontFamily: 'Poppins' }}>

      {/* Hospital Info Section */}
      <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-5 py-3">
          <h3 className="text-xl font-bold text-white">Hospital Information</h3>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <div>
              <label className={LBL}>Hospital Name</label>
              <input className={INP} value={basicInfo.name} onChange={e=>setBasic('name', e.target.value)} placeholder="e.g., City General Hospital" />
            </div>
            <div>
              <label className={LBL}>Hospital Code <span className="normal-case text-gray-400 text-xs">(max 9)</span></label>
              <input className={INP} maxLength={9} value={basicInfo.hospitalCode} onChange={e=>setBasic('hospitalCode', e.target.value)} placeholder="CGH" />
            </div>
            <div>
              <label className={LBL}>Registration No. <span className="text-red-500">*</span> <span className="normal-case text-gray-400 text-xs">(max 8)</span></label>
              <input className={INP} maxLength={8} value={basicInfo.registrationNumber} onChange={e=>setBasic('registrationNumber', e.target.value)} placeholder="MH-12345" />
            </div>
            <div>
              <label className={LBL}>Hospital Type</label>
              <select className={INP} value={basicInfo.type} onChange={e=>setBasic('type', e.target.value)}>
                <option value="Private">Private</option>
                <option value="Government">Government</option>
                <option value="Trust">Trust</option>
                <option value="Corporate">Corporate</option>
                <option value="Specialty">Specialty</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={LBL}>Registration Type</label>
              <select className={INP} value={basicInfo.registrationType} onChange={e=>setBasic('registrationType', e.target.value)}>
                <option value="Proprietorship">Proprietorship</option>
                <option value="Partnership">Partnership</option>
                <option value="Private Limited">Private Limited</option>
                <option value="Public Limited">Public Limited</option>
                <option value="Limited Liability Partnership">Limited Liability Partnership</option>
                <option value="Trust">Trust</option>
                <option value="Society">Society</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-5 py-3">
          <h3 className="text-xl font-bold text-white">Contact Details</h3>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <div>
              <label className={LBL}>Email</label>
              <input type="email" className={INP} value={basicInfo.contact.email} onChange={e=>setBasic('contact.email', e.target.value)} placeholder="admin@hospital.com" />
            </div>
            <div>
              <label className={LBL}>Phone</label>
              <input className={INP} value={basicInfo.contact.phone} onChange={e=>setBasic('contact.phone', e.target.value)} placeholder="022-12345678" />
            </div>
            <div>
              <label className={LBL}>Mobile</label>
              <input className={INP} value={basicInfo.contact.mobile} onChange={e=>setBasic('contact.mobile', e.target.value)} placeholder="9876543210" />
            </div>
            <div>
              <label className={LBL}>Emergency No.</label>
              <input className={INP} value={basicInfo.contact.emergencyNumber} onChange={e=>setBasic('contact.emergencyNumber', e.target.value)} placeholder="108" />
            </div>
          </div>
          <div>
            <label className={LBL}>Website</label>
            <input className={INP} value={basicInfo.contact.website} onChange={e=>setBasic('contact.website', e.target.value)} placeholder="https://example.com" />
          </div>
        </div>
      </div>

      {/* Tax Details Section */}
      <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-5 py-3">
          <h3 className="text-xl font-bold text-white">Tax Details</h3>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <div>
              <label className={LBL}>GST Number</label>
              <input className={INP} value={basicInfo.taxDetails.gstNumber} onChange={e=>setBasic('taxDetails.gstNumber', e.target.value)} placeholder="27AABCU9603R1ZM" />
            </div>
            <div>
              <label className={LBL}>PAN Number</label>
              <input className={INP} value={basicInfo.taxDetails.panNumber} onChange={e=>setBasic('taxDetails.panNumber', e.target.value)} placeholder="AABCU9603R" />
            </div>
            <div>
              <label className={LBL}>TAN Number <span className="normal-case text-gray-400 text-xs">(optional)</span></label>
              <input className={INP} value={basicInfo.taxDetails.tanNumber} onChange={e=>setBasic('taxDetails.tanNumber', e.target.value)} placeholder="MUMH12345" />
            </div>
            <div>
              <label className={LBL}>CIN Number <span className="normal-case text-gray-400 text-xs">(optional)</span></label>
              <input className={INP} value={basicInfo.taxDetails.cinNumber} onChange={e=>setBasic('taxDetails.cinNumber', e.target.value)} placeholder="U85110MH2010" />
            </div>
          </div>
        </div>
      </div>

      {/* Address Section */}
      <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-5 py-3">
          <h3 className="text-xl font-bold text-white">Address</h3>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={LBL}>Street</label>
              <input className={INP} placeholder="123 Main Street" value={basicInfo.address.street} onChange={e=>setBasic('address.street', e.target.value)} />
            </div>
            <div>
              <label className={LBL}>Area</label>
              <input className={INP} placeholder="Andheri West" value={basicInfo.address.area} onChange={e=>setBasic('address.area', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <div>
              <label className={LBL}>City</label>
              <input className={INP} placeholder="Mumbai" value={basicInfo.address.city} onChange={e=>setBasic('address.city', e.target.value)} />
            </div>
            <div>
              <label className={LBL}>State</label>
              <input className={INP} placeholder="Maharashtra" value={basicInfo.address.state} onChange={e=>setBasic('address.state', e.target.value)} />
            </div>
            <div>
              <label className={LBL}>ZIP Code</label>
              <input className={INP} placeholder="400001" value={basicInfo.address.zipCode} onChange={e=>setBasic('address.zipCode', e.target.value)} />
            </div>
            <div>
              <label className={LBL}>Country</label>
              <input className={INP} placeholder="India" value={basicInfo.address.country} onChange={e=>setBasic('address.country', e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-2">
        {settingsId ? (
          <button onClick={handleSaveBasic} disabled={saving} className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] disabled:from-gray-400 disabled:to-gray-400 text-white px-8 py-3 rounded-xl font-bold transition-all text-sm shadow-lg hover:shadow-xl disabled:cursor-not-allowed">
            {saving ? <Icon name="Loader2" className="animate-spin" size={16} /> : <Icon name="Save" size={16} />}
            <span className="text-base font-bold">Save Basic Info</span>
          </button>
        ) : (
          <button onClick={handleInitialCreate} disabled={saving} className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] disabled:from-gray-400 disabled:to-gray-400 text-white px-8 py-3 rounded-xl font-bold transition-all text-sm shadow-lg hover:shadow-xl disabled:cursor-not-allowed">
            {saving ? <Icon name="Loader2" className="animate-spin" size={16} /> : <Icon name="Plus" size={16} />}
            <span className="text-base font-bold">Create Hospital Settings</span>
          </button>
        )}
      </div>
    </div>
  );

  const renderLogoForm = () => (
    <div className="space-y-6" style={{ fontFamily: 'Poppins' }}>
      <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-5 py-3">
          <h3 className="text-xl font-bold text-white">Hospital Logo</h3>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Preview */}
            <div>
              <p className="text-md font-semibold text-gray-600 uppercase tracking-wide mb-3">Current Logo</p>
              <div className="w-full h-64 bg-[#9DC9D4]/10 border-2 border-[#9DC9D4]/40 rounded-xl flex items-center justify-center overflow-hidden">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo" className="max-w-full max-h-full object-contain" />
                ) : (
                  <div className="text-center text-gray-400">
                    <Icon name="Image" size={52} className="mx-auto mb-3 text-[#9DC9D4]" />
                    <p className="text-md font-semibold text-gray-500">No logo uploaded</p>
                  </div>
                )}
              </div>
            </div>

            {/* Upload Area */}
            <div>
              <p className="text-md font-semibold text-gray-600 uppercase tracking-wide mb-3">Upload New Logo</p>
              <div
                className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all ${
                  dragActive ? 'border-[#2D869C] bg-[#9DC9D4]/10' : 'border-gray-200 hover:border-[#2D869C]'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  id="logo-upload"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                  className="hidden"
                />
                <label htmlFor="logo-upload" className="cursor-pointer">
                  <Icon name="Upload" size={36} className="mx-auto text-[#9DC9D4] mb-3" />
                  <p className="text-md font-semibold text-gray-600 mb-1">
                    <span className="text-[#2D869C]">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-sm text-gray-400 mt-1">PNG, JPG, GIF, WebP — max 5MB</p>
                </label>

                {selectedFile && (
                  <div className="mt-4 p-3 bg-[#9DC9D4]/20 border border-[#9DC9D4] rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-[#2D869C] font-semibold text-sm truncate">
                        <Icon name="CheckCircle2" size={16} />
                        <span className="truncate">{selectedFile.name}</span>
                      </div>
                      <button
                        onClick={(e) => { e.preventDefault(); setSelectedFile(null); setLogoPreview(''); }}
                        className="text-red-500 hover:text-red-700 ml-2 shrink-0"
                      >
                        <Icon name="X" size={16} />
                      </button>
                    </div>
                    <p className="text-xs text-[#2D869C] mt-1">{(selectedFile.size / 1024).toFixed(2)} KB</p>
                  </div>
                )}
              </div>

              <button
                onClick={handleUploadLogo}
                disabled={saving || !selectedFile}
                className="mt-4 w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] disabled:from-gray-400 disabled:to-gray-400 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl disabled:cursor-not-allowed"
              >
                {saving ? <Icon name="Loader2" className="animate-spin" size={16} /> : <Icon name="Upload" size={16} />}
                <span className="text-base font-bold">{saving ? 'Uploading...' : 'Upload Logo'}</span>
              </button>

              <div className="mt-4 p-3 bg-[#9DC9D4]/20 border border-[#9DC9D4]/50 rounded-xl">
                <p className="text-sm font-semibold text-[#2D869C] flex items-center gap-1.5">
                  <Icon name="Info" size={14} />
                  The logo will be optimized and stored securely in the cloud.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderInvoiceForm = () => (
    <div className="space-y-6" style={{ fontFamily: 'Poppins' }}>

      {/* Invoice Settings */}
      <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-5 py-3">
          <h3 className="text-xl font-bold text-white">Invoice Configuration</h3>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Invoice Prefix</label>
              <input className={INP} value={invoiceConfig.invoicePrefix} onChange={e=>setInvoiceConfig(prev=>({ ...prev, invoicePrefix: e.target.value }))} placeholder="INV" />
            </div>
            <div>
              <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Default Tax Rate (%)</label>
              <input type="number" min={0} max={100} step="0.01" className={INP} value={invoiceConfig.defaultTaxRate} onChange={e=>setInvoiceConfig(prev=>({ ...prev, defaultTaxRate: Number(e.target.value||0) }))} placeholder="18" />
            </div>
            <div>
              <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Payment Terms</label>
              <input className={INP} value={invoiceConfig.paymentTerms} onChange={e=>setInvoiceConfig(prev=>({ ...prev, paymentTerms: e.target.value }))} placeholder="Payment due within 30 days" />
            </div>
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Invoice Footer</label>
            <textarea className={`${INP} resize-none`} rows={3} value={invoiceConfig.invoiceFooter} onChange={e=>setInvoiceConfig(prev=>({ ...prev, invoiceFooter: e.target.value }))} placeholder="Thank you for your visit." />
          </div>
        </div>
      </div>

      {/* Bank Details */}
      <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-5 py-3">
          <h3 className="text-xl font-bold text-white">Bank Details</h3>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Bank Name</label>
              <input className={INP} placeholder="State Bank of India" value={invoiceConfig.bankDetails.bankName} onChange={e=>setInvoiceConfig(prev=>({ ...prev, bankDetails: { ...prev.bankDetails, bankName: e.target.value } }))} />
            </div>
            <div>
              <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Account Name</label>
              <input className={INP} placeholder="City General Hospital" value={invoiceConfig.bankDetails.accountName} onChange={e=>setInvoiceConfig(prev=>({ ...prev, bankDetails: { ...prev.bankDetails, accountName: e.target.value } }))} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Account Number</label>
              <input className={INP} placeholder="123456789012" value={invoiceConfig.bankDetails.accountNumber} onChange={e=>setInvoiceConfig(prev=>({ ...prev, bankDetails: { ...prev.bankDetails, accountNumber: e.target.value } }))} />
            </div>
            <div>
              <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">IFSC Code</label>
              <input className={INP} placeholder="SBIN0001234" value={invoiceConfig.bankDetails.ifscCode} onChange={e=>setInvoiceConfig(prev=>({ ...prev, bankDetails: { ...prev.bankDetails, ifscCode: e.target.value } }))} />
            </div>
            <div>
              <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Branch Name</label>
              <input className={INP} placeholder="Andheri West" value={invoiceConfig.bankDetails.branchName} onChange={e=>setInvoiceConfig(prev=>({ ...prev, bankDetails: { ...prev.bankDetails, branchName: e.target.value } }))} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <button onClick={handleSaveInvoice} disabled={saving} className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] disabled:from-gray-400 disabled:to-gray-400 text-white px-8 py-3 rounded-xl font-bold transition-all text-sm shadow-lg hover:shadow-xl disabled:cursor-not-allowed">
          {saving ? <Icon name="Loader2" className="animate-spin" size={16} /> : <Icon name="Save" size={16} />}
          <span className="text-base font-bold">Save Invoice Config</span>
        </button>
      </div>
    </div>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <Icon name="Loader2" className="animate-spin text-[#2D869C]" size={26} />
          <span className="ml-3 text-gray-500 text-sm" style={{ fontFamily: 'Poppins' }}>Loading settings...</span>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-12 gap-6">
        {/* Side tabs */}
        <div className="col-span-12 lg:col-span-3">
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-2" style={{ fontFamily: 'Poppins' }}>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest px-3 py-2">Settings</p>
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-lg font-medium mb-1 transition-all duration-150 ${
                  activeTab === t.id
                    ? 'bg-[#2D869C] text-white shadow-sm'
                    : 'text-gray-600 hover:bg-[#9DC9D4]/20 hover:text-[#2D869C]'
                }`}
              >
                <Icon name={t.icon} size={20} />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="col-span-12 lg:col-span-9">
          <div className="bg-white border-2 border-gray-200 rounded-xl shadow-sm p-6">
            {error && (
              <div className="mb-5 p-4 rounded-xl bg-red-50 border-2 border-red-200 text-red-700 font-semibold text-md flex items-center gap-2" style={{ fontFamily: 'Poppins' }}>
                <Icon name="AlertCircle" size={16} />
                {error}
              </div>
            )}
            {success && (
              <div className="mb-5 p-4 rounded-xl bg-[#9DC9D4]/20 border-2 border-[#9DC9D4] text-[#2D869C] font-semibold text-md flex items-center gap-2" style={{ fontFamily: 'Poppins' }}>
                <Icon name="CheckCircle2" size={16} />
                {success}
              </div>
            )}

            {activeTab === 'basic' && renderBasicForm()}
            {activeTab === 'logo' && renderLogoForm()}
            {activeTab === 'invoice' && renderInvoiceForm()}
          </div>
        </div>
      </div>
    );
  };

  return (
    <RoleGuard allowed={["Admin"]}>
      <div className="min-h-screen bg-gray-50" style={{ fontFamily: 'Poppins' }}>
        <NavigationPreventer />
        <TopNavBar 
          user={user}
          activeTab={navTab}
          onTabChange={handleNavTabChange}
        />

        <main className="pt-20 px-4 lg:px-6 pb-10">
          <div className="space-y-6">
            {/* Page Header */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <span className="w-9 h-9 rounded-full bg-[#9DC9D4]/30 flex items-center justify-center">
                    <Icon name="Settings" size={18} className="text-[#2D869C]" />
                  </span>
                  Hospital Settings
                </h1>
                <p className="text-sm text-gray-500 mt-1 tracking-wide">Manage hospital/organization information and invoice configuration</p>
              </div>
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#9DC9D4]/20 text-[#2D869C] text-xs font-semibold uppercase tracking-wider">
                <Icon name="Shield" size={13} />
                Admin Only
              </div>
            </div>

            {!settingsId && !loading && (
              <div className="p-4 border border-yellow-200 rounded-xl bg-yellow-50 text-yellow-800 text-sm flex items-center gap-2">
                <Icon name="AlertTriangle" size={15} />
                No settings found. Fill in Basic Info and click "Create Hospital Settings" to initialize.
              </div>
            )}

            {renderContent()}
          </div>
        </main>
      </div>
    </RoleGuard>
  );
}
